import { filter, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { UntypedFormGroup } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { AppIpInfoService } from '@shared/ui-components/au-phone-input/app-ip-info.service';
import { IUser } from '@app/shared/interfaces/user.interface';
import {
	COUNTRY_CODES,
	CountryCodeObject,
} from '@app/pages/settings/edit-profile/country-codes';

export class CodeAndPhoneValidationLogic {
	selectedCountryCodeObject: CountryCodeObject;
	filteredOptions$ = of([]);
	profileForm: UntypedFormGroup;

	protected destroy$ = new Subject<void>();
	private mappedCountryCodes = COUNTRY_CODES;
	protected phoneNameAlias: string;

	constructor(protected appIpInfoService: AppIpInfoService) {}

	protected startProcessingCodeAndPhone() {
		this.startFilteringCountryCodesForAutocomplete();
		this.startAutoFormatOfCountryCodeValue();
		this.watchPhoneAndUpdateCodeValidity();
	}

	protected startAutoFormatOfCountryCodeValue(): void {
		this.countryCodeControl.valueChanges
			.pipe(
				takeUntil(this.destroy$),
				filter(value => !!value)
			)
			.subscribe(value => {
				this.countryCodeControl.setValue(this.processCodeValue(value), {
					emitEvent: false,
				});
			});
	}

	private watchPhoneAndUpdateCodeValidity() {
		this.phoneControl.valueChanges
			.pipe(
				takeUntil(this.destroy$),
				filter(value => !!value)
			)
			.subscribe(value => {
				// when we have no mask for phone field
				// we still need to restrict user to type any symbols except digit
				if (!this.selectedCountryCodeObject) {
					const clearedValue = value.replace(/[^0-9 ]/g, '');
					this.phoneControl.setValue(clearedValue, { emitEvent: false });
				}
				this.countryCodeControl.updateValueAndValidity();
			});
	}

	private processCodeValue(value: string): string {
		// code always starts from +
		// also we delete all symbols except digits and space
		if (value !== '+' && value !== '') {
			value = value.replace(/[^0-9 ]/g, '');
			value = '+' + value;
		}

		// we delete phone number mask when code is empty
		if (value === '+' || value === '') {
			this.selectedCountryCodeObject = null;
		}

		return value;
	}

	protected startFilteringCountryCodesForAutocomplete(): void {
		this.filteredOptions$ = this.countryCodeControl.valueChanges.pipe(
			takeUntil(this.destroy$),
			startWith(''),
			tap(value => {
				this.findAndSetCountryCodeObject(value, 'dial_code');
			}),
			map(value => this.filterCodes(value))
		);
	}

	private filterCodes(value: string): CountryCodeObject[] {
		const filterValue = value?.toLowerCase();
		return this.mappedCountryCodes.filter(option =>
			option.dial_code.includes(filterValue)
		);
	}

	protected findAndSetCountryCodeObject(value: string, property: string): void {
		const codeObject = COUNTRY_CODES.find(item => item[property] === value);

		if (codeObject) {
			this.selectedCountryCodeObject = codeObject;
		}
	}

	protected setCountryCode(code: string = 'NO') {
		const codeItem = COUNTRY_CODES.find(c => c.code === code);

		this.profileForm.patchValue({
			countryCode: codeItem
				? codeItem.dial_code
				: this.selectedCountryCodeObject.dial_code,
		});
	}

	protected getAndSetCountryCode(profile: IUser) {
		if (!profile?.countryCode) {
			this.detectLocationByIpAndSetCountryCodeObject();
		} else {
			this.findAndSetCountryCodeObject(profile.countryCode, 'dial_code');
		}
	}

	protected detectLocationByIpAndSetCountryCodeObject() {
		this.appIpInfoService.getCountryCodeByUserIp().then(
			res => {
				this.setCountryCode(res?.country_code);
			},
			() => this.setCountryCode()
		);
	}

	get countryCodeControl() {
		return this.profileForm.controls.countryCode;
	}

	get phoneControl() {
		return this.profileForm.controls[this.phoneNameAlias];
	}
}
